
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import type { Book } from "../types";

export function useBooks() {
  const [books, setBooks] = useState<Book[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const booksPerPage = 10;

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const from = (currentPage - 1) * booksPerPage;
      const to = from + booksPerPage - 1;

      const { count } = await supabase
        .from("books")
        .select("id", { count: "exact" });

      const { data, error } = await supabase
        .from("books")
        .select("*")
        .order("order", { ascending: true })
        .range(from, to);

      if (error) {
        throw error;
      }

      if (count) {
        setTotalPages(Math.ceil(count / booksPerPage));
      }

      setBooks(data || []);
    } catch (err) {
      console.error("Error fetching books:", err);
      setError("Failed to load books. Please try again later.");
      toast({
        title: "Error",
        description: "Failed to load books",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  useEffect(() => {
    const filtered = books.filter(
      (book) =>
        book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (book.description &&
          book.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredBooks(filtered);
  }, [books, searchQuery]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    books: filteredBooks,
    loading,
    error,
    currentPage,
    totalPages,
    setCurrentPage,
    handleSearch,
    refreshBooks: fetchBooks
  };
}
